import { useMemo } from 'react'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import type {
  GetGlobalServiceInformationTemplatesNormalized,
  Item,
  TextItem,
} from '@messenger/types'

import { serviceInformationTemplatesAtom } from '@/atoms/_editorAtoms'
import { v4 as uuidv4 } from 'uuid'
import { zLayers } from '@/utils/styling/zLayers'
import { itemAtomFamily, itemIdAtom, unsavedChangesAtom } from '@/atoms'
import { useEditorInsertTextBox } from '@/hooks/editor/useEditorInsertTextBox'
import { useEditorInsertHtmlTextBox } from '@/hooks/editor/useEditorInsertHtmlTextBox'
import { useEditorInsertsLotus } from '@/hooks/editor/useEditorInsertsLotus'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { parseEditorServiceTemplateContent } from '@/utils/editor-text-box/editorTextBoxAdapter'
import ServiceTemplatesMenu from './ServiceTemplatesMenu'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'

type ServiceTemplateInsertProps = {
  setShow: (show: boolean) => void
  setCreatingTemplate: (creatingTemplate: boolean) => void
  setEditingTemplate: (editingTemplate: boolean) => void
  setSelectedTemplate: (
    template: GetGlobalServiceInformationTemplatesNormalized | null,
  ) => void
}

const ServiceTemplateInsert = ({
  setShow,
  setCreatingTemplate,
  setEditingTemplate,
  setSelectedTemplate,
}: ServiceTemplateInsertProps) => {
  useEditorProbeLifetime('ServiceTemplateInsert')
  const templates = useRecoilValue(serviceInformationTemplatesAtom)
  const itemIds = useRecoilValue(itemIdAtom)

  const isAdmin = useIsAdmin()
  const insertEditorTextBox = useEditorInsertTextBox()
  const templateInsertsLotus = useEditorInsertsLotus()
  const insertHtmlTextBox = useEditorInsertHtmlTextBox()
  const handleError = useErrorHandler('ServiceTemplateInsert')

  // A template saved as a Lotus arrangement inserts a Lotus box, so it is
  // offered only where a text insert makes one; an HTML template is offered
  // everywhere, and goes in as whichever kind the insert rule makes.
  const offeredTemplates = useMemo(
    () =>
      templateInsertsLotus
        ? templates
        : templates.filter(
            (template) =>
              parseEditorServiceTemplateContent(template.template) === null,
          ),
    [templates, templateInsertsLotus],
  )

  const addToItems = useRecoilCallback(({ set }) => (item: Item) => {
    set(itemAtomFamily(item.id), item as Item)
    set(itemIdAtom, [...itemIds, item.id])
    set(unsavedChangesAtom, true)
  })

  const closeMenu = () => {
    setShow(false)
    setCreatingTemplate(false)
  }

  const handleSelectTemplate = (
    template: GetGlobalServiceInformationTemplatesNormalized,
  ) => {
    const editorContent = parseEditorServiceTemplateContent(template.template)
    if (editorContent !== null) {
      void insertEditorTextBox(editorContent.blocks).then(closeMenu)
      return
    }

    const uuid = uuidv4()

    const textObj: Item = {
      id: uuid,
      type: 'text' as const,
      x: 0,
      y: 0,
      z: zLayers.content,
      width: 250,
      height: 250,

      text: {
        value: template.template,
      },
      edits: {},
      properties: {},
    }
    if (templateInsertsLotus) {
      // A template the converter refuses goes in as this TinyMCE item.
      void insertHtmlTextBox(textObj as TextItem)
        .then((outcome) => {
          if (outcome !== null) closeMenu()
        })
        .catch((error: Error) => handleError(error.message, error))
      return
    }
    addToItems(textObj)
    closeMenu()
  }

  return (
    <ServiceTemplatesMenu
      templates={offeredTemplates}
      onTemplateSelect={handleSelectTemplate}
      hasCloseButton={false}
      allowActions={isAdmin}
      onCreateTemplate={() => setCreatingTemplate(true)}
      onEditTemplate={(template) => {
        setEditingTemplate(true)
        setSelectedTemplate(template)
      }}
    />
  )
}

export default ServiceTemplateInsert
